import { useEffect, useRef, useState } from "react";

/** true si el usuario pidió reducir el movimiento. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Observa si el elemento entra en pantalla. Con `once` (por defecto) se queda
 * en true después de la primera vez: sirve para disparar animaciones de
 * entrada. Con `once: false` sigue el estado (para pausar shaders).
 */
export function useInView<T extends Element = HTMLDivElement>({
  once = true,
  threshold = 0.25,
  rootMargin = "0px",
}: { once?: boolean; threshold?: number; rootMargin?: string } = {}) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) io.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, threshold, rootMargin]);

  return { ref, inView };
}

/**
 * Cuenta hasta `target` cuando `start` pasa a true (desde 0 la primera vez;
 * si después cambia `target`, desde el valor que se veía).
 */
export function useCountUp(
  target: number,
  start: boolean,
  { duration = 1200, delay = 0, decimals = 0 } = {},
): string {
  const [value, setValue] = useState(0);
  const shown = useRef(0);

  useEffect(() => {
    if (!start) return;
    if (prefersReducedMotion()) {
      shown.current = target;
      setValue(target);
      return;
    }
    const from = shown.current;
    let raf = 0;
    const t0 = performance.now() + delay;
    const tick = (now: number) => {
      const p = Math.min(1, Math.max(0, (now - t0) / duration));
      const eased = 1 - Math.pow(1 - p, 3);
      shown.current = from + (target - from) * eased;
      setValue(shown.current);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start, duration, delay]);

  return value.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Línea de tiempo de una sola pasada: desde que `start` pasa a true devuelve
 * el índice de la última marca (ms) cumplida. Con prefers-reduced-motion
 * salta a la última.
 */
export function useOnceStep(start: boolean, marks: readonly number[]): number {
  const [step, setStep] = useState(0);
  const [reduced] = useState(prefersReducedMotion);
  const key = marks.join(",");
  useEffect(() => {
    if (reduced || !start) return;
    const timers = marks.map((at, i) =>
      window.setTimeout(() => setStep(i), at),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
    // `key` resume `marks`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, key, reduced]);
  return reduced ? marks.length - 1 : step;
}

/**
 * Línea de tiempo en bucle: devuelve el índice de la última marca (ms desde el
 * inicio del ciclo) que ya pasó. Solo corre mientras `active` (sección en
 * pantalla); al volver a entrar arranca de cero. Con prefers-reduced-motion
 * devuelve `staticStep` (el estado final) y no programa nada.
 */
export function useLoopStep(
  active: boolean,
  marks: readonly number[],
  duration: number,
  staticStep = marks.length - 1,
): number {
  const [step, setStep] = useState(0);
  const [reduced] = useState(prefersReducedMotion);
  const key = marks.join(",");

  useEffect(() => {
    if (reduced || !active) return;
    let timers: number[] = [];
    const cycle = () => {
      setStep(0);
      timers = marks
        .map((at, i) => (i === 0 ? 0 : window.setTimeout(() => setStep(i), at)))
        .filter(Boolean);
      timers.push(window.setTimeout(cycle, duration));
    };
    cycle();
    return () => timers.forEach((t) => window.clearTimeout(t));
    // `key` resume `marks`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key, duration, reduced]);

  return reduced ? staticStep : step;
}

/**
 * Reloj en bucle (ms dentro del ciclo) a ~`fps` cuadros por segundo, para
 * animaciones que dependen del tiempo fino (texto que se escribe). Solo corre
 * mientras `active`; con prefers-reduced-motion devuelve `duration - 1` (el
 * estado final) sin programar nada.
 */
export function useLoopClock(
  active: boolean,
  duration: number,
  fps = 30,
): number {
  const [t, setT] = useState(0);
  const [reduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (reduced || !active) return;
    let raf = 0;
    let last = 0;
    const t0 = performance.now();
    const frame = 1000 / fps;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (now - last < frame) return;
      last = now;
      setT((now - t0) % duration);
    };
    setT(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, duration, fps, reduced]);

  return reduced ? duration - 1 : t;
}
